import { RESUME_LOCATION_KEY } from '@/constants/session-keys';
import { ROUTES } from '@/constants/routes';

// Long enough to cover stepping away from the desk, short enough that a tab
// left open overnight doesn't jump somewhere the user has forgotten about.
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

// Resuming to any of these either bounces the user straight back out or
// restores nothing they'd recognise as where they were. Embed routes are
// iframe surfaces with their own auth, so both share the one prefix.
const EMBED_PREFIX = ROUTES.EMBED.CHAT.split('/').slice(0, 2).join('/');
const AUTH0_CALLBACK_PATH = '/callback';
const NON_RESUMABLE_PATHS = [ROUTES.LOGIN, AUTH0_CALLBACK_PATH, EMBED_PREFIX];

function isResumable(path: string): boolean {
  // Rejects protocol-relative and absolute URLs, which would send the user
  // off-origin on what is supposed to be a same-tab restore.
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const pathname = path.split(/[?#]/)[0];
  return !NON_RESUMABLE_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function currentLocation(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

/**
 * Stores where the user is so the next sign-in can put them back. sessionStorage
 * rather than localStorage: it is scoped to this tab, so two tabs signed out
 * together each resume their own page, and it survives the identity-provider
 * round trip because that returns to the same tab and origin.
 */
export function rememberResumeLocation(path: string = currentLocation()): void {
  try {
    if (!isResumable(path)) {
      clearResumeLocation();
      return;
    }
    sessionStorage.setItem(
      RESUME_LOCATION_KEY,
      JSON.stringify({ path, at: Date.now() }),
    );
  } catch {
    // ignore storage failures — losing the resume point costs a redirect, and
    // failing the sign-out over it would cost the session
  }
}

/**
 * Reads the stored location and consumes it, so a later sign-in in the same tab
 * lands on the default page instead of jumping back to a stale one.
 */
export function takeResumeLocation(): string | null {
  try {
    const raw = sessionStorage.getItem(RESUME_LOCATION_KEY);
    sessionStorage.removeItem(RESUME_LOCATION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at);
    if (!Number.isFinite(at) || Date.now() - at > MAX_AGE_MS) return null;

    const path = parsed?.path;
    return typeof path === 'string' && isResumable(path) ? path : null;
  } catch {
    return null;
  }
}

export function clearResumeLocation(): void {
  try {
    sessionStorage.removeItem(RESUME_LOCATION_KEY);
  } catch {
    // ignore storage failures
  }
}
